import { useState } from 'react';
import { useGlobalStateSelector, words } from 'globalState';
import {
  buildCollectibleWordRecords,
  buildPlayerStatsRecords,
  formatWinRate,
} from './playerStats';
import iconExpand from './expand_all_24dp_000000_FILL0_wght400_GRAD0_opsz24.svg';
import iconCollapse from './collapse_all_24dp_000000_FILL0_wght400_GRAD0_opsz24.svg';

function PlayerStats() {
  const activeAddress = useGlobalStateSelector(({ state }) => state.active.addressMain);
  const userRecord = useGlobalStateSelector(({ state }) => state.user[state.active.addressMain]);
  const language = useGlobalStateSelector(({ state }) => state.language);
  const statisticsParameters = useGlobalStateSelector(({ state }) => state.config.clientParameters.statisticsParameters);
  const collectibleWordImageHostPath = useGlobalStateSelector(({ state }) => state.config.clientParameters.collectibleWordImageHostPath);
  const navigate = useGlobalStateSelector(({ navigate }) => navigate);

  let content;

  if (
    activeAddress === undefined ||
    activeAddress === null ||
    activeAddress === '' ||
    userRecord === undefined
  ) {
    content = null;
  }
  else {
    const playerStatsRecords = buildPlayerStatsRecords({
      statistics: userRecord.statistics,
    });
    const collectibleWordRecords = buildCollectibleWordRecords({
      collection: userRecord.collection,
      collectibleWordsMaster: userRecord.collectibleWordsMaster,
      language: 'japanese',
      collectibleWordImageHostPath,
    });

    content =
    <div className='flexColumn alignItemsCenter widthMax'>
      <PlayerStatsGameRecords
        playerStatsRecords={playerStatsRecords}
        language={language}
        statisticsParameters={statisticsParameters}
      />
      <CollectibleWordRecords
        collectibleWordRecords={collectibleWordRecords}
        language={language}
      />
    </div>;
  }

  return (
    <div>
      <div className='z200FixedZero widthMax backgroundColorWhiteTransparent '>
        <div className='flexRow justifyContentFlexEnd marginBottom1' >
          <button
            className='button2'
            onClick={ () => navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/top' : '/top') }
          >
            { words.back[language] }
          </button>
        </div>
      </div>
      <div className='widthMax height6 ' />
      <div className='flexColumn alignItemsCenter widthMax'>
        <div className='font2 margin1'>
          { userRecord?.basicInformation?.userName ?? words.pleaseLogin[language] }
        </div>
        { content }
      </div>
    </div>
  );
}

function PlayerStatsGameRecords({ playerStatsRecords, language, statisticsParameters }) {
  return (
    <div className='flexColumn alignItemsCenter widthMax'>
      <div className='font1p5 margin1'>
        { words.duelRecord[language] }
      </div>
      { playerStatsRecords.map(periodRecord => (
        <PeriodRecord
          key={periodRecord.period}
          periodRecord={periodRecord}
          language={language}
          statisticsParameters={statisticsParameters}
        />
      )) }
    </div>
  );
}

function buildTotalRecord({ records }) {
  const total = records.reduce((accumulator, record) => ({
    win: accumulator.win + record.win,
    lose: accumulator.lose + record.lose,
    draw: accumulator.draw + record.draw,
  }), {
    win: 0,
    lose: 0,
    draw: 0,
  });
  const numberOfDuelsForWinRate = total.win + total.lose;
  const winRate = numberOfDuelsForWinRate > 0 ? total.win / numberOfDuelsForWinRate : 0;

  return {
    ...total,
    winRate,
  };
}

function getPeriodTotalRecord({ periodRecord }) {
  return buildTotalRecord({
    records: periodRecord.gameModes.flatMap(gameModeRecord => (
      gameModeRecord.difficultyModes.map(difficultyModeRecord => difficultyModeRecord.record)
    )),
  });
}

function getGameModeTotalRecord({ gameModeRecord }) {
  return buildTotalRecord({
    records: gameModeRecord.difficultyModes.map(difficultyModeRecord => difficultyModeRecord.record),
  });
}

function getSortedDifficultyModeRecords({ difficultyModeRecords }) {
  const difficultyModeOrder = {
    advanced: 0,
    simple: 1,
  };

  return [...difficultyModeRecords].sort((a, b) => (
    difficultyModeOrder[a.difficultyMode] - difficultyModeOrder[b.difficultyMode]
  ));
}

function ExpandCollapseButton({ isExpanded, onClick }) {
  const switchIcon = isExpanded ? iconCollapse : iconExpand;

  return (
    <button
      className='button1'
      type='button'
      onClick={onClick}
    >
      <img className='size2x2' src={switchIcon} alt='' />
    </button>
  );
}

function PeriodRecord({ periodRecord, language, statisticsParameters }) {
  const [isExpanded, setIsExpanded] = useState(false);
  let periodLabel = words[periodRecord.period][language];

  if (periodRecord.period === 'monthly') {
    periodLabel = `${periodLabel} ${statisticsParameters.thisYear}/${statisticsParameters.thisMonth}`;
  }

  const periodTotalRecord = getPeriodTotalRecord({
    periodRecord,
  });

  return (
    <div className='boxMonacotto1 flexColumn alignItemsCenter width90PC margin1'>
      <RecordRow
        label={periodLabel}
        record={periodTotalRecord}
        language={language}
        toggleButton={
          <ExpandCollapseButton
            isExpanded={isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        }
      />
      { isExpanded ? periodRecord.gameModes.map(gameModeRecord => (
        <GameModeRecord
          key={gameModeRecord.gameMode}
          gameModeRecord={gameModeRecord}
          language={language}
        />
      )) : null }
    </div>
  );
}

function GameModeRecord({ gameModeRecord, language }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const gameModeWords = {
    human: words.pvp[language],
    computer: words.pvc[language],
  };
  const gameModeTotalRecord = getGameModeTotalRecord({
    gameModeRecord,
  });
  const sortedDifficultyModeRecords = getSortedDifficultyModeRecords({
    difficultyModeRecords: gameModeRecord.difficultyModes,
  });

  return (
    <div className='flexColumn alignItemsCenter widthMax marginTopBottom0p5'>
      <RecordRow
        label={gameModeWords[gameModeRecord.gameMode]}
        record={gameModeTotalRecord}
        language={language}
        toggleButton={
          <ExpandCollapseButton
            isExpanded={isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        }
      />
      { isExpanded ? (
        <div className='flexColumn alignItemsCenter widthMax'>
          { sortedDifficultyModeRecords.map(difficultyModeRecord => (
            <DifficultyModeRecord
              key={difficultyModeRecord.difficultyMode}
              difficultyModeRecord={difficultyModeRecord}
              language={language}
            />
          )) }
        </div>
      ) : null }
    </div>
  );
}

function DifficultyModeRecord({ difficultyModeRecord, language }) {
  const difficultyModeWords = {
    simple: words.simpleMode[language],
    advanced: words.advancedMode[language],
  };
  return (
    <RecordRow
      label={difficultyModeWords[difficultyModeRecord.difficultyMode]}
      record={difficultyModeRecord.record}
      language={language}
    />
  );
}

function RecordRow({ label, record, language, toggleButton }) {
  const numberOfDuels = record.win + record.lose + record.draw;
  const duelResultRows = [
    { label: words.wins[language], value: record.win },
    { label: words.losses[language], value: record.lose },
    { label: words.draws[language], value: record.draw },
    { label: words.numberOfDuels[language], value: numberOfDuels },
    { label: words.winRate[language], value: formatWinRate({ winRate: record.winRate }) },
  ];

  return (
    <div className='widthMax'>
      {/* PC */}
      <div className='visibleLargeOrMore widthMax'>
        <div className='boxHistory flexRow justifyContentSpaceBetween alignItemsCenter textCenter'>
          <div className='marginSide1 widthMin10'>
            { label }
          </div>
          { duelResultRows.map(duelResultRow => (
            <div key={duelResultRow.label} className='marginSide1 widthMin5'>
              { `${duelResultRow.label}: ${duelResultRow.value}` }
            </div>
          )) }
          <div className='marginSide1 widthMin5'>
            { toggleButton }
          </div>
        </div>
      </div>
      {/* SP */}
      <div className='visibleMiddleOrLess boxMonacotto1 flexColumn alignItemsFlexStart textLeft widthMax'>
        <div className='flexRow justifyContentSpaceBetween alignItemsCenter widthMax'>
          <div>{ words.gameRecord[language] }</div>
          <div>{ label }</div>
          <div>{ toggleButton }</div>
        </div>
        { duelResultRows.map(duelResultRow => (
          <div key={duelResultRow.label} className='flexRow justifyContentSpaceBetween alignItemsCenter widthMax'>
            <div>{ duelResultRow.label }</div>
            <div>{ duelResultRow.value }</div>
          </div>
        )) }
      </div>
    </div>
  );
}

function CollectibleWordRecords({ collectibleWordRecords, language }) {
  let records;

  if (collectibleWordRecords.length < 1) {
    records = words.nothing[language];
  }
  else {
    records = collectibleWordRecords.map(collectibleWordRecord => (
      <CollectibleWordRecord
        key={collectibleWordRecord.word}
        collectibleWordRecord={collectibleWordRecord}
      />
    ));
  }

  return (
    <div className='flexColumn alignItemsCenter widthMax'>
      <div className='font1p5 margin1'>
        { words.collectibleWords[language] }
      </div>
      <div className='boxMonacotto1 flexColumn alignItemsCenter width90PC margin1'>
        <div className='flexRow justifyContentCenter width98PC'>
          { records }
        </div>
      </div>
    </div>
  );
}

function CollectibleWordRecord({ collectibleWordRecord }) {
  return (
    <div className='backgroundColorMonacottoPale borderMonacotto flexColumn alignItemsCenter margin0p5 padding0p5 textCenter'>
      <img className='width5 marginBottom0p5' src={collectibleWordRecord.imageUrl} alt={collectibleWordRecord.face} />
      <div>
        { collectibleWordRecord.face }
      </div>
      <div>
        { collectibleWordRecord.count }
      </div>
    </div>
  );
}

export {
  PlayerStats,
};
